import { Sparkle } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useRevertSculpt } from "~/api/queries";
import type { SculptLatest } from "~/api/types";
import { Button } from "~/foundation/components/Button";
import { Text } from "~/foundation/components/Text";

export interface SculptRevertRowProps {
  documentId: string;
  latest: SculptLatest;
  canUpdate: boolean;
}

/**
 * Shows while the newest sculpt is still revertible. If the document has drifted since the
 * apply, revert never blind-restores — the server stages the restore as a proposal instead,
 * and the row says so up front.
 */
export function SculptRevertRow({ documentId, latest, canUpdate }: SculptRevertRowProps) {
  const { t } = useTranslation();
  const revert = useRevertSculpt();

  const onRevert = () => {
    revert.mutate(
      { documentId, opId: latest.opId },
      {
        onSuccess: (outcome) => {
          if (outcome.status === "proposed") {
            toast.info(t("The document changed since — the restore was staged as a proposal"));
          } else {
            toast.success(t("Sculpt reverted"));
          }
        },
      },
    );
  };

  return (
    <div className="flex items-center gap-2 rounded-md bg-bg-secondary px-3 py-1.5">
      <Sparkle size={14} className="text-accent" aria-hidden />
      <Text variant="small" className="min-w-0 flex-1 truncate text-text-secondary">
        {latest.thinker.thinkerName
          ? t("Sculpted by {{name}}", { name: latest.thinker.thinkerName })
          : t("Sculpted by AI")}
      </Text>
      {canUpdate ? (
        <Button
          variant="ghost"
          size="sm"
          disabled={revert.isPending}
          title={
            latest.revertMode === "propose"
              ? t("The document changed since — revert opens a proposal")
              : undefined
          }
          onClick={onRevert}
        >
          {t("Revert")}
        </Button>
      ) : null}
    </div>
  );
}
